// School API service — real API calls replacing mock data

import { apiRequest } from './api';

// ── Raw API response types ──────────────────────────────────

export interface SchoolPrincipal {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
}

export interface SchoolApiRecord {
    id: string;
    organization_id: string;
    name: string;
    school_code: string;
    principal_user_id: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
    contact_phone: string | null;
    contact_email: string | null;
    established_year: number | null;
    website: string | null;
    is_active: boolean;
    metadata: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
    boards_count: number;
    principal: SchoolPrincipal | null;
    boards?: {
        id: number;
        name: string;
        code: string;
        slug: string;
        description: string | null;
        website: string | null;
        logo_url: string | null;
        is_active: boolean;
        affiliation: {
            affiliation_number: string | null;
            affiliated_since: string | null;
            expires_at: string | null;
        };
    }[];
}

export interface SchoolListResponse {
    success: boolean;
    message: string;
    data: SchoolApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export interface StaffApiRecord {
    id: string;
    employee_id: string;
    designation_id: string | null;
    department_id: string | null;
    user: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string | null;
    };
    department: { id: string; name: string } | null;
    designation: { id: string; name: string; is_teaching_role: boolean } | null;
}

export type StaffOption = {
    userId: string;
    name: string;
    email: string | null;
    designation: string | null;
};

// ── API Functions ───────────────────────────────────────────

/**
 * List schools with optional search and pagination.
 */
export async function fetchSchools(params?: {
    page?: number;
    perPage?: number;
    search?: string;
}): Promise<SchoolListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.search) query.set('search', params.search);

    const qs = query.toString();
    const res = await apiRequest<SchoolApiRecord[]>(`/api/schools${qs ? `?${qs}` : ''}`);

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as SchoolListResponse).meta,
    };
}

/**
 * Get a single school by ID.
 */
export async function fetchSchool(id: string): Promise<SchoolApiRecord> {
    const res = await apiRequest<SchoolApiRecord>(`/api/schools/${id}`);
    return res.data;
}

/**
 * Create a new school.
 */
export async function createSchool(data: Record<string, unknown>): Promise<SchoolApiRecord> {
    const res = await apiRequest<SchoolApiRecord>('/api/schools', {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return res.data;
}

/**
 * Update an existing school.
 */
export async function updateSchool(id: string, data: Record<string, unknown>): Promise<SchoolApiRecord> {
    const res = await apiRequest<SchoolApiRecord>(`/api/schools/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
    return res.data;
}

/**
 * Delete a school.
 */
export async function deleteSchool(id: string): Promise<void> {
    await apiRequest(`/api/schools/${id}`, { method: 'DELETE' });
}

/**
 * Search staff members for principal selection.
 * Uses GET /staff with a name query param, then maps to a simple option shape.
 */
export async function searchStaff(query: string): Promise<StaffOption[]> {
    const params = new URLSearchParams({ name: query, per_page: '20' });
    const res = await apiRequest<StaffApiRecord[]>(`/api/staff?${params.toString()}`);
    return res.data.map((s) => ({
        userId: s.user.id,
        name: `${s.user.first_name}${s.user.last_name ? ' ' + s.user.last_name : ''}`.trim(),
        email: s.user.email,
        designation: s.designation?.name ?? null,
    }));
}

/**
 * Fetch all staff members for principal select dropdown.
 */
export async function fetchStaffOptions(): Promise<StaffOption[]> {
    const res = await apiRequest<StaffApiRecord[]>('/api/staff?per_page=100');
    return res.data.map((s) => ({
        userId: s.user.id,
        name: `${s.user.first_name}${s.user.last_name ? ' ' + s.user.last_name : ''}`.trim(),
        email: s.user.email,
        designation: s.designation?.name ?? null,
    }));
}

// ── Board types & API ───────────────────────────────────────

export type BoardApiRecord = {
    id: number;
    name: string;
    code: string;
    slug: string;
    description: string | null;
    website: string | null;
    logo_url: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

export type FormBoardEntry = {
    board_id: number;
    affiliation_number: string;
    affiliated_since: string;
    expires_at: string;
};

/**
 * Fetch all boards.
 */
export async function fetchBoards(): Promise<BoardApiRecord[]> {
    const res = await apiRequest<BoardApiRecord[]>('/api/boards');
    return res.data;
}

// ── Helpers ─────────────────────────────────────────────────

/**
 * Derive a display name from the principal object.
 */
function getPrincipalName(principal: SchoolPrincipal | null): string | null {
    if (!principal) return null;
    return `${principal.first_name}${principal.last_name ? ' ' + principal.last_name : ''}`.trim();
}

/**
 * Map an API school record to the flat UI-friendly shape used by SchoolManagement.
 */
export function mapSchoolToUi(s: SchoolApiRecord) {
    return {
        id: s.id,
        name: s.name,
        school_code: s.school_code,
        principal_user_id: s.principal_user_id,
        principal_name: getPrincipalName(s.principal),
        established_year: s.established_year,
        contact_email: s.contact_email,
        contact_phone: s.contact_phone,
        website: s.website,
        address: s.address,
        city: s.city,
        state: s.state,
        postal_code: s.postal_code,
        country: s.country,
        is_active: s.is_active,
        metadata: s.metadata,
        boards_count: s.boards_count,
        created_at: s.created_at,
        updated_at: s.updated_at,
    };
}

export type SchoolUi = ReturnType<typeof mapSchoolToUi>;
